/**
 * Appointment History Component
 * Displays patient's past and future appointments
 */

import { Calendar, Clock, User } from 'lucide-react';
import type { DentalOfficeAppointment } from '../../types';
import { formatDate } from '../../lib/utils';
import { LoadingSpinner } from '../LoadingSpinner';

interface AppointmentHistoryProps {
  appointments: DentalOfficeAppointment[] | undefined;
  isLoading: boolean;
}

export function AppointmentHistory({ appointments, isLoading }: AppointmentHistoryProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner text="Hleð tímapöntunum..." />
      </div>
    );
  }

  if (!appointments || appointments.length === 0) {
    return (
      <div className="text-center py-12">
        <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Engar tímapantanir skráðar</p>
      </div>
    );
  }

  // Separate future and past appointments
  const now = new Date();
  const futureAppointments = appointments.filter(
    (apt) => new Date(apt.appointment_datetime) >= now
  );
  const pastAppointments = appointments.filter(
    (apt) => new Date(apt.appointment_datetime) < now
  );

  const renderAppointment = (appointment: DentalOfficeAppointment) => (
    <div
      key={appointment.id}
      className="bg-white border border-gray-200 rounded-lg p-4 hover:border-brand-primary transition-colors"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="font-semibold text-gray-900">{appointment.appointment_type}</h4>
          {appointment.notes && (
            <p className="text-sm text-gray-600 mt-1">{appointment.notes}</p>
          )}
        </div>
        <span
          className={`px-3 py-1 rounded-full text-xs font-medium ${
            appointment.status === 'scheduled'
              ? 'bg-info/10 text-info'
              : appointment.status === 'confirmed'
              ? 'bg-success/10 text-success'
              : appointment.status === 'completed'
              ? 'bg-gray-100 text-gray-800'
              : appointment.status === 'cancelled'
              ? 'bg-error/10 text-error'
              : 'bg-warning/10 text-warning'
          }`}
        >
          {appointment.status === 'scheduled'
            ? 'Áætlað'
            : appointment.status === 'confirmed'
            ? 'Staðfest'
            : appointment.status === 'completed'
            ? 'Lokið'
            : appointment.status === 'cancelled'
            ? 'Hætt við'
            : 'Kom ekki'}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          <span>{formatDate(appointment.appointment_datetime)}</span>
        </div>

        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4" />
          <span>
            {new Date(appointment.appointment_datetime).toLocaleTimeString('is-IS', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>
      </div>

      {appointment.provider_id && (
        <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
          <User className="w-4 h-4" />
          <span>Tannlæknir #{appointment.provider_id}</span>
        </div>
      )}

      {appointment.duration_minutes && (
        <p className="mt-2 text-sm text-gray-600">
          Lengd: {appointment.duration_minutes} mínútur
        </p>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Future Appointments */}
      {futureAppointments.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Komandi tímapantanir</h3>
          <div className="space-y-4">
            {futureAppointments.map(renderAppointment)}
          </div>
        </div>
      )}

      {/* Past Appointments */}
      {pastAppointments.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Fyrri tímapantanir</h3>
          <div className="space-y-4">
            {pastAppointments.map(renderAppointment)}
          </div>
        </div>
      )}
    </div>
  );
}
